import Layout from "../layout";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import axios from "axios";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { ROOT_API } from "../../api/config";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useIntl } from "react-intl";
import { ErrorCode } from "../../api/ErrorCode";
import { useSelector, useDispatch } from "react-redux";
import { GoogleLogin } from "@react-oauth/google";
import Divider from "@mui/material/Divider";
import { updateUserInformation } from "../../stores/users/userSlice";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";

const schema = yup.object().shape({
  full_name: yup.string().required(<FormattedMessage id="name_not_blank" />),
  email: yup.string().required(<FormattedMessage id="email_not_blank" />),
  password: yup
    .string()
    .test(
      "password",
      <FormattedMessage id="password_at_least" />,
      (value) => value.length >= 6
    )
    .test(
      "password-max",
      <FormattedMessage id="password_at_most" />,
      (value) => value.length <= 24
    ),
});

const MAX_COUNTER = 60;

const Register = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: "onSubmit",
    resolver: yupResolver(schema),
  });

  const intl = useIntl();

  const navigation = useNavigate();
  const dispatch = useDispatch();

  // Carry the SEO landing-page intent (/grad/<slug> CTA: ?grad=&ref=) across the signup →
  // verify → login → add-address flow. register-service reads `seo_grad` to pre-seed the picker.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const grad = params.get("grad");
    const ref = params.get("ref");
    if (grad) localStorage.setItem("seo_grad", grad);
    if (ref) localStorage.setItem("seo_ref", ref);
  }, []);

  // Google sign-up — same backend flow as login (auth/google upserts by email),
  // mirrored from pages/login. Button hidden when the client id isn't configured.
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

  const showGoogleError = () => {
    Swal.fire({
      icon: "error",
      title: "Oops...",
      text: intl.formatMessage({ id: "there_are_error_occured" }),
    });
  };

  const onGoogleSuccess = async (credentialResponse) => {
    if (!credentialResponse || !credentialResponse.credential) {
      showGoogleError();
      return;
    }
    setIsLoading(true);
    try {
      const res = await axios.post(`${ROOT_API}apis/auth/google`, {
        credential: credentialResponse.credential,
      });
      if (res && res.data) {
        localStorage.setItem("access_token", res.data.data.access_token);
        dispatch(updateUserInformation(res.data.data.user_data));
        // From a /grad/<slug> CTA, land on add-address (town pre-seeded); otherwise home.
        navigation(localStorage.getItem("seo_grad") ? "/register-service" : "/pocetna");
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text:
          error &&
          error.response &&
          error.response.data &&
          error.response.data.error_code
            ? intl.formatMessage({
                id: ErrorCode[error.response.data.error_code],
              })
            : intl.formatMessage({
                id: "there_are_error_occured",
              }),
      });
    }
    setIsLoading(false);
  };

  const [email, setEmail] = useState("");

  const [code, setCode] = useState("");

  const [isOpenVerification, setIsOpenVerification] = useState(false);

  const selectedLocale =
    useSelector((state) => state.lang.value) ||
    localStorage.getItem("lang") ||
    "hr";

  const [isLoading, setIsLoading] = useState(false);

  const [showPassword, setShowPassword] = useState(false);

  const [counter, setCounter] = useState(MAX_COUNTER);

  const [isGettingOtp, setIsGettingOtp] = useState(false);

  useEffect(() => {
    let interval = setInterval(() => {
      setCounter(counter - 1);
    }, 1000);

    if (!counter) {
      clearInterval(interval);
      return;
    }

    return () => {
      clearInterval(interval);
    };
  }, [counter]);

  const handleResendOtp = async () => {
    const axiosObject = axios.create({
      headers: {
        "Content-Type": "application/json",
        lang: selectedLocale,
      },
    });
    setIsGettingOtp(true);
    try {
      await axiosObject.post(`${ROOT_API}apis/generate-otp`, { email });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text:
          error?.response?.status === 429
            ? intl.formatMessage({ id: "too_many_requests" })
            : error?.response?.data?.error_code
            ? intl.formatMessage({ id: ErrorCode[error.response.data.error_code] })
            : intl.formatMessage({ id: "there_are_error_occured" }),
      });
    }
    setCounter(MAX_COUNTER);
    setIsGettingOtp(false);
  };

  const verifyError = (error) => {
    Swal.fire({
      icon: "error",
      title: "Oops...",
      text:
        error?.response?.status === 429
          ? intl.formatMessage({ id: "too_many_requests" })
          : error?.response?.data?.error_code
          ? intl.formatMessage({ id: ErrorCode[error.response.data.error_code] })
          : intl.formatMessage({ id: "there_are_error_occured" }),
    });
  };

  const finishSignup = () => {
    setIsOpenVerification(false);
    Swal.fire({
      icon: "success",
      title: intl.formatMessage({ id: "register_successfully" }),
      confirmButtonText: "OK",
    }).then((result) => {
      if (result.isConfirmed) navigation("/login");
    });
  };

  // Email OTP -> account goes live immediately. Phone verification is NOT part of signup; it's a
  // paid, in-account opt-in step for SMS alerts (request/confirm-phone-verification).
  const handleSubmitOtpCode = async () => {
    setIsLoading(true);
    try {
      const res = await axios.post(`${ROOT_API}apis/verify`, { email, otp: code });
      const payload = res?.data?.data;
      if (payload?.access_token) {
        // Auto-login: the verify step now returns a JWT, so store it and drop the user straight
        // on add-address (the picker pre-seeds the town from seo_grad) instead of /login.
        localStorage.setItem("access_token", payload.access_token);
        dispatch(updateUserInformation(payload.user_data));
        setIsOpenVerification(false);
        navigation("/register-service");
      } else if (res?.data) {
        finishSignup(); // fallback: backend without the token (old behaviour → /login)
      }
    } catch (error) {
      verifyError(error);
    }
    setIsLoading(false);
  };

  const onSubmit = async (data) => {
    const axiosObject = axios.create({
      headers: {
        "Content-Type": "application/json",
        lang: selectedLocale,
      },
    });
    setIsLoading(true);
    setCounter(MAX_COUNTER);
    try {
      data.role = "user";
      // SEO attribution: forward the /grad/<slug> CTA source captured on mount (localStorage).
      const seoRef = localStorage.getItem("seo_ref");
      const seoGrad = localStorage.getItem("seo_grad");
      if (seoRef) data.ref = seoRef;
      if (seoGrad) data.grad = seoGrad;
      const res = await axiosObject.post(`${ROOT_API}apis/register`, data);
      if (res && res.data) {
        // ref is consumed; keep seo_grad so register-service can pre-seed the address picker.
        if (seoRef) localStorage.removeItem("seo_ref");
        setEmail(data.email);
        setCode("");
        setIsOpenVerification(true);
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text:
          error &&
          error.response &&
          error.response.data &&
          error.response.data.error_code
            ? intl.formatMessage({
                id: ErrorCode[error.response.data.error_code],
              })
            : intl.formatMessage({
                id: "there_are_error_occured",
              }),
      });
    }
    setIsLoading(false);
  };

  return (
    <Layout>
      <div className="general-form">
        <div className="container">
          <div className="general-form-main">
            <form
              className="general-form-main-form p-36"
              onSubmit={handleSubmit(onSubmit)}
            >
              <img
                src={`${process.env.PUBLIC_URL}/assets/mascot/beaver-wave.png`}
                alt=""
                aria-hidden="true"
                style={{ width: 92, height: "auto", display: "block", margin: "0 auto 6px" }}
              />
              <p className="font-bold font-20 text-center mb-22">
                <FormattedMessage id="register" />
              </p>
              {googleClientId && (
                <>
                  <Box
                    sx={{ display: "flex", justifyContent: "center" }}
                    aria-label={intl.formatMessage({ id: "login_with_google" })}
                  >
                    <GoogleLogin
                      onSuccess={onGoogleSuccess}
                      onError={showGoogleError}
                      text="signup_with"
                      locale={selectedLocale}
                      width="280"
                    />
                  </Box>
                  <Divider sx={{ my: 2, fontSize: 13, color: "var(--ink-soft)" }}>
                    {intl.formatMessage({ id: "or" })}
                  </Divider>
                </>
              )}
              <div className="general-form-main-form_group mb-12">
                <input
                  className="px-10 py-12 radius-4 font-16"
                  placeholder={intl.formatMessage({ id: "full_name" })}
                  type="text"
                  {...register("full_name")}
                />
                {errors.full_name && (
                  <p className="error font-14 mt-2">
                    {errors.full_name.message}
                  </p>
                )}
              </div>

              <div className="general-form-main-form_group mb-12">
                <input
                  type="email"
                  className="px-10 py-12 radius-4 font-16"
                  placeholder={intl.formatMessage({ id: "email" })}
                  {...register("email")}
                />
                {errors.email && (
                  <p className="error font-14 mt-2">{errors.email.message}</p>
                )}
              </div>
              <div className="general-form-main-form_group mb-12">
                <input
                  className="px-10 py-12 radius-4 font-16"
                  placeholder={intl.formatMessage({ id: "password" })}
                  type={showPassword ? "text" : "password"}
                  {...register("password")}
                />
                <span
                  className="font-13 pointer"
                  style={{
                    color: "var(--tungsten-deep)",
                    display: "inline-block",
                    marginTop: 6,
                  }}
                  onClick={() => setShowPassword((v) => !v)}
                >
                  {intl.formatMessage({
                    id: showPassword ? "hide_password" : "show_password",
                  })}
                </span>
                {errors.password && (
                  <p className="error font-14 mt-2">
                    {errors.password.message}
                  </p>
                )}
              </div>
              <Box
                sx={{
                  display: isLoading ? "flex" : "none",
                  justifyContent: "center",
                }}
              >
                <CircularProgress />
              </Box>
              <button
                type="submit"
                className="btn-cta general-form-main-form_submit mt-40"
              >
                {intl.formatMessage({ id: "submit" })}
              </button>
              <div className="general-form-main-form_register mt-20 text-center pointer">
                <Link
                  to="/login"
                  style={{ textDecoration: "none", color: "var(--tungsten-deep)" }}
                >
                  {intl.formatMessage({ id: "switch_to_login" })}
                </Link>
              </div>
            </form>
          </div>
          <div>
            <Dialog open={isOpenVerification}>
              <DialogTitle>
                {intl.formatMessage({ id: "email_verification" })}
              </DialogTitle>
              <DialogContent>
                <DialogContentText>
                  {intl.formatMessage({
                    id: "please_enter_verification_code_sent_to",
                  })}
                  : {email}
                </DialogContentText>
                <div className="pt-24">
                  <div className="general-form-main-form_group mb-12">
                    <input
                      className="px-10 py-12 radius-4 font-16"
                      placeholder={intl.formatMessage({
                        id: "code",
                      })}
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      onChange={(e) => {
                        setCode(e.target.value);
                      }}
                    />
                  </div>
                </div>
                <div
                  className="general-form-main-form_register mt-20 text-center"
                  style={{
                    color: !counter && "var(--tungsten-deep)",
                    cursor: !counter ? "pointer" : "not-allowed",
                  }}
                >
                  {counter > 0 ? (
                    <div>
                      {intl.formatMessage({ id: "resend_after" })} ({counter}){" "}
                      {intl.formatMessage({ id: "seconds" })}
                    </div>
                  ) : isGettingOtp ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : (
                    <div
                      onClick={() => {
                        handleResendOtp();
                      }}
                    >
                      {intl.formatMessage({ id: "resend_verification_code" })}
                    </div>
                  )}
                </div>
              </DialogContent>
              <Box
                sx={{
                  display: isLoading ? "flex" : "none",
                  justifyContent: "center",
                }}
              >
                <CircularProgress />
              </Box>
              <DialogActions
                style={{ marginBottom: "12px", padding: "0px 24px" }}
              >
                <button
                  className="btn-cta"
                  type="button"
                  onClick={() => {
                    setIsOpenVerification(false);
                  }}
                >
                  {intl.formatMessage({ id: "cancel" })}
                </button>
                <button
                  className="btn-cta"
                  style={{
                    cursor: code ? "pointer" : "not-allowed",
                  }}
                  onClick={() => {
                    handleSubmitOtpCode();
                  }}
                  disabled={!code}
                >
                  {intl.formatMessage({ id: "confirm" })}
                </button>
              </DialogActions>
            </Dialog>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Register;
